import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { clearAllOfflineData } from '../lib/offlineDb'

type UserRole = {
  id: number
  name: string
  label?: string
}

type User = {
  id: number
  username: string
  email?: string
  role?: UserRole | string
  permissions?: string[]
  tenant?: number
  // Sprint B Frontend 2026-05-15: flat shortcut from UserMeSerializer.
  // Source-of-truth lives on Tenant.currency_code; this field is read-only.
  tenant_currency_code?: string
  // UAE-Patient-Felder 2026-05-26: flat shortcut from UserMeSerializer for
  // Tenant.country_code. Drives region-conditional rendering of Emirates-ID
  // / insurance fields vs. Iraqi tribe_name/father_name/grandfather_name in
  // PatientNew + PatientDetail. Defaults to "IQ" backend-side; UAE-tenants
  // must be explicitly set to "AE" (see seed_uae_demo).
  tenant_country_code?: 'IQ' | 'AE'
  // Master-Tenant marker — gates the Promote-to-GitHub button.
  // Customer-clinic tenants always see false here; only the Mashfa-Team
  // tenant (designated via `manage.py set_master_tenant`) returns true.
  tenant_is_master?: boolean
  // Sprint L PR-L1/L4 — per-doctor specialty code (e.g. 'dentistry',
  // 'gynecology', 'general', ''). Drives the PatientDetail specialty-tab
  // filter PER USER instead of per tenant. Source of truth is the
  // Doctor-edit dropdown (DoctorDetailSerializer); /api/me/ exposes this
  // read-only.
  specialization?: string
}

/**
 * Sprint I-5 Part 2 — login can branch into a 2FA step.
 *
 *  - `authenticated`: classic happy path; user state is set and the caller
 *    can navigate('/').
 *  - `otp_required`: password was OK, user already has a confirmed TOTP
 *    device. The caller must collect a 6-digit code (or recovery code)
 *    and call `verifyOtp` / `verifyRecovery` with the `session_token`.
 *  - `setup_required`: password was OK but the admin has never enrolled.
 *    The caller starts the setup flow with `setup2FA(setup_token)`.
 */
export type LoginOutcome =
  | { kind: 'authenticated' }
  | { kind: 'otp_required'; session_token: string }
  | { kind: 'setup_required'; setup_token: string }

export type SetupResponse = {
  qr_code_base64: string
  secret: string
  confirm_token: string
}

export type ConfirmResponse = {
  recovery_codes: string[]
  warning: string
}

type AuthContextValue = {
  user: User | null
  initializing: boolean
  login: (username: string, password: string) => Promise<LoginOutcome>
  verifyOtp: (sessionToken: string, otpCode: string) => Promise<void>
  verifyRecovery: (sessionToken: string, recoveryCode: string) => Promise<void>
  setup2FA: (setupTokenOrAuth: string | null) => Promise<SetupResponse>
  confirm2FA: (confirmToken: string, otpCode: string) => Promise<ConfirmResponse>
  resetUser2FA: (userId: number) => Promise<void>
  logout: () => void
  authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function getCsrfToken(): string | null {
  const match = /csrftoken=([^;]+)/.exec(document.cookie)
  return match ? match[1] : null
}

async function doRefresh(): Promise<void> {
  const csrf = getCsrfToken()
  const res = await fetch('/api/auth/refresh/', {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'X-CSRFToken': csrf } : undefined,
  })
  if (!res.ok) throw new Error('refresh failed')
}

async function fetchMe(): Promise<User> {
  const res = await fetch('/api/auth/me/', { credentials: 'include' })
  if (!res.ok) throw new Error('me failed')
  return res.json()
}

/** Treat the body as JSON if it parses; throw with the server message
 *  otherwise. Keeps the call-sites tidy. */
async function postJson<T = unknown>(
  url: string, body: Record<string, unknown>, csrf?: string | null,
): Promise<{ ok: boolean; status: number; data: T | null }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (csrf) headers['X-CSRFToken'] = csrf
  const res = await fetch(url, {
    method: 'POST', headers, credentials: 'include',
    body: JSON.stringify(body),
  })
  let data: T | null = null
  try { data = (await res.json()) as T } catch { /* empty body is fine */ }
  return { ok: res.ok, status: res.status, data }
}

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [initializing, setInitializing] = useState<boolean>(true)

  useEffect(() => {
    const init = async () => {
      setInitializing(true)
      try {
        const data = await fetchMe()
        setUser(data)
      } catch {
        try {
          await doRefresh()
          const data = await fetchMe()
          setUser(data)
        } catch {
          setUser(null)
        }
      } finally {
        setInitializing(false)
      }
    }
    init()
  }, [])

  /** Finish a login that resulted in JWT cookies being set: load /me/,
   *  cache-clear, return. Used by the password-direct path AND by
   *  verifyOtp / verifyRecovery. */
  const finishAuthenticated = async () => {
    queryClient.clear()
    const me = await fetchMe()
    setUser(me)
    queryClient.invalidateQueries({ queryKey: ['update-check'] })
  }

  const login = async (username: string, password: string): Promise<LoginOutcome> => {
    const { ok, status, data } = await postJson<{
      otp_required?: boolean
      setup_required?: boolean
      session_token?: string
      setup_token?: string
    }>('/api/auth/login/', { username, password })
    if (!ok) {
      // 4xx: surface a stable error code so the form can i18n it.
      // 400 = invalid creds, 429 = rate-limited.
      throw new Error(status === 429 ? 'too_many_attempts' : 'login_failed')
    }
    if (data?.otp_required && data.session_token) {
      return { kind: 'otp_required', session_token: data.session_token }
    }
    if (data?.setup_required && data.setup_token) {
      return { kind: 'setup_required', setup_token: data.setup_token }
    }
    // Classic happy path: JWT cookies set, no 2FA gate.
    await finishAuthenticated()
    return { kind: 'authenticated' }
  }

  const verifyOtp = async (sessionToken: string, otpCode: string) => {
    const { ok, status } = await postJson('/api/auth/login/verify-otp/', {
      session_token: sessionToken, otp_code: otpCode,
    })
    if (!ok) throw new Error(status === 429 ? 'too_many_attempts' : 'otp_invalid')
    await finishAuthenticated()
  }

  const verifyRecovery = async (sessionToken: string, recoveryCode: string) => {
    const { ok, status } = await postJson('/api/auth/login/recovery/', {
      session_token: sessionToken, recovery_code: recoveryCode,
    })
    if (!ok) throw new Error(status === 429 ? 'too_many_attempts' : 'recovery_invalid')
    await finishAuthenticated()
  }

  const setup2FA = async (setupToken: string | null): Promise<SetupResponse> => {
    const csrf = getCsrfToken()
    const body: Record<string, unknown> = {}
    if (setupToken) body.setup_token = setupToken
    const { ok, data } = await postJson<SetupResponse>(
      '/api/auth/2fa/setup/', body, csrf,
    )
    if (!ok || !data) throw new Error('setup_failed')
    return data
  }

  const confirm2FA = async (
    confirmToken: string, otpCode: string,
  ): Promise<ConfirmResponse> => {
    const csrf = getCsrfToken()
    const { ok, data } = await postJson<ConfirmResponse>(
      '/api/auth/2fa/confirm/',
      { confirm_token: confirmToken, otp_code: otpCode },
      csrf,
    )
    if (!ok || !data) throw new Error('confirm_failed')
    return data
  }

  const resetUser2FA = async (userId: number) => {
    const csrf = getCsrfToken()
    const { ok, status } = await postJson(
      `/api/admin/users/${userId}/reset-2fa/`, {}, csrf,
    )
    if (!ok) {
      let errorCode = 'reset_failed'
      if (status === 403) errorCode = 'forbidden'
      else if (status === 404) errorCode = 'not_found'
      throw new Error(errorCode)
    }
  }

  const logout = React.useCallback(() => {
    const csrf = getCsrfToken()
    fetch('/api/auth/logout/', {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'X-CSRFToken': csrf } : undefined,
    }).catch(() => { /* ignore network errors */ })
    queryClient.clear()
    clearAllOfflineData().catch(() => { /* ignore — best-effort */ })
    setUser(null)
  }, [queryClient])

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!user) return
    const TIMEOUT = 30 * 60 * 1000 // 30 min
    let timer: ReturnType<typeof setTimeout>
    const reset = () => {
      clearTimeout(timer)
      timer = setTimeout(() => logout(), TIMEOUT)
    }
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'] as const
    events.forEach(e => document.addEventListener(e, reset))
    reset()
    return () => {
      clearTimeout(timer)
      events.forEach(e => document.removeEventListener(e, reset))
    }
  }, [user, logout])

  const authFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const initCopy: RequestInit = { ...init, credentials: 'include' }
    let res = await fetch(input, initCopy)
    if (res.status === 401) {
      try {
        await doRefresh()
        res = await fetch(input, initCopy)
      } catch {
        setUser(null)
      }
    }
    return res
  }

  const value = useMemo<AuthContextValue>(() => ({
    user, initializing,
    login, verifyOtp, verifyRecovery, setup2FA, confirm2FA, resetUser2FA,
    logout, authFetch,
  // login/verify/setup/confirm/reset and authFetch are closure-stable
  // wrt the state they need; only user/initializing/logout need to
  // invalidate the memoised value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, initializing, logout])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
